import type {
  Account,
  Address,
  BaseErrorType,
  CallErrorType,
  Chain,
  ContractFunctionArgs,
  UnionEvaluate,
  UnionOmit,
} from "viem"
import type {
  SimulateContractErrorType,
  SimulateContractParameters,
} from "viem/actions"

import type { contractBook } from "@eulerxyz/core-sdk"

import type { GetConnectorClientErrorType, SelectChains } from "@wagmi/core"
import type { ChainIdParameter, ConnectorParameter } from "@wagmi/core/internal"

import { getConnectorClient } from "@wagmi/core"

import type { SimulateError } from "../errors"
import type { TransactionItems } from "../factories"
import {
  type SimulateProps,
  SimulationFactory,
} from "../factories/SimulationFactory"
import type { ErrorType, ExtendedConfig } from "../types"

export type AccountValues<T> = {
  [account: Address]: T
}

export type LiabilityValues = {
  isController: boolean
  liabilityValue: bigint
  queryFailure: boolean
}

export type Collaterals = {
  shares: bigint
  queryFailure: boolean
  queryReason: string
  collateralValueBorrowing: bigint
  collateralValueLiquidation: bigint
  isEnabledCollateral: boolean
}

type BatchArgs = ContractFunctionArgs<
  typeof contractBook.evc.abi,
  "nonpayable" | "payable",
  "batchSimulation"
>

export type SimulationParameters<
  config extends ExtendedConfig = ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends
    | config["chains"][number]["id"]
    | undefined = config["chains"][number]["id"],
  chains extends readonly Chain[] = SelectChains<config, chainId>,
> = {
  [key in keyof chains]: UnionEvaluate<
    UnionOmit<
      SimulateContractParameters<
        typeof contractBook.evc.abi,
        "batchSimulation",
        BatchArgs,
        chains[key],
        chains[key],
        Account | Address
      >,
      "chain" | "abi" | "functionName" | "args" | "address"
    >
  > &
    ChainIdParameter<config, chainId> &
    ConnectorParameter &
    SimulateProps<transactions>
}[number]

export type SimulationReturnType = Awaited<ReturnType<typeof simulation>>

export type SimulationErrorType =
  // getConnectorClient()
  | GetConnectorClientErrorType
  // base
  | BaseErrorType
  | ErrorType
  // viem
  | SimulateContractErrorType
  // evc
  | SimulateError
  //
  | CallErrorType

export async function simulation<
  config extends ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends config["chains"][number]["id"] | undefined = undefined,
>(
  config: config,
  parameters: SimulationParameters<config, transactions, chainId>,
) {
  const {
    chainId,
    connector,
    transactions,
    prevTransactions,
    currentAccount,
    currentVault,
    forceCheckVaults,
  } = parameters

  let account: Address | Account
  if (parameters.account) account = parameters.account
  else {
    const connectorClient = await getConnectorClient(config, {
      chainId,
      connector,
    })
    account = connectorClient.account
  }

  const client = config.getClient({ chainId })
  if (!account && client.account) account = client.account

  const address = typeof account === "string" ? account : account.address

  const simulationFactory = new SimulationFactory(config, client, address)

  return simulationFactory.simulate({
    transactions,
    prevTransactions,
    currentAccount,
    currentVault,
    forceCheckVaults,
  })
}
